/**
 * Utility functions for API requests
 */

import { config } from '../config/environment';
import { authedApiFetch } from '../services/tokenRefresh';
import {
  fakeGetApprovalLessonDetail,
  fakeGetApprovalQueue,
  fakeGetLearnContent,
  fakeGetShiftLeads,
  fakeGetTenantTranslationLanguages,
  fakeListTenants,
  fakeSetTenantTranslationLanguages,
  fakeUpdateTenant,
  fakeTestGradeBlank,
  fakeMoveDrill,
} from '../services/fakeTenantData';

type ApiOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  headers?: Record<string, string>;
  body?: unknown;
};

/**
 * feat-196 (B3) — fake-mode short-circuit for the TENANT surface only.
 *
 * Scoped deliberately narrow. It answers exactly these paths — `GET /api/tenant`,
 * `PUT /api/tenant/{id}`, (feat-260) `GET`/`PUT
 * /api/tenant/{id}/translation-languages`, (feat-284) `GET
 * /api/tenant/{id}/learn-content`, and (feat-299) the two approval READS under
 * it plus the five Edit-mode text PUTs `/quiz-review` reuses, plus (feat-321)
 * `GET /api/tenant/{id}/locations/{lid}/shift-leads`, plus (feat-325) `POST
 * /api/admin/question-items/{id}/test-grade`, plus (feat-361) `POST
 * /api/learn-drills/{id}/move` — and nothing else. Every
 * other path, including the `/api/tenant/{id}/locations`, `/roles` and
 * `/servers` children, returns null here and falls through to the real
 * network path.
 *
 * feat-260 EDITED THIS PARAGRAPH rather than adding beside it, and feat-284 has
 * done the same. It once read "it answers `GET /api/tenant` and `PUT
 * /api/tenant/{id}` and NOTHING else", which each new branch made false — and a
 * comment stating a scope is the thing the next person reads INSTEAD of counting
 * the branches, so a stale one is worse than none. Whoever adds a fifth branch
 * owes this sentence the same edit.
 *
 * Each `/{id}…` match is anchored by SEGMENT COUNT plus a literal final segment —
 * a `$`-equivalent. That is load-bearing, not stylistic: a loose
 * `startsWith('/api/tenant/')` would swallow the child routes and silently break
 * the rest of the dashboard in fake mode. feat-260's branch keeps the same
 * discipline (`length === 4` and `segments[3] === 'translation-languages'`), so it
 * cannot match `/api/tenant/{id}/locations` however the id is shaped.
 *
 * Returns null when it does not handle the request.
 */
function fakeTenantResponse(url: string, method: string, body: unknown): unknown | null {
  if (!config.useDummyData) return null;
  const path = url.split('?')[0];
  if (path === '/api/tenant' && method === 'GET') {
    return fakeListTenants();
  }
  // Exactly `/api/tenant/{id}` — 4 segments after splitting on '/', no trailing child.
  const segments = path.split('/').filter(Boolean); // ['api','tenant','{id}']
  if (segments.length === 3 && segments[0] === 'api' && segments[1] === 'tenant' && method === 'PUT') {
    const updated = fakeUpdateTenant(segments[2], (body || {}) as Record<string, never>);
    // A missing tenant returns an empty object rather than throwing — the caller
    // re-reads the list either way, and fake mode should never be the thing that
    // surfaces a 404 the real backend wouldn't.
    return updated ?? {};
  }
  // feat-260 — exactly `/api/tenant/{id}/translation-languages` (ADR 0001 §7.3),
  // GET and PUT. The segment-count anchoring above is deliberate and is kept
  // here: `length === 4` plus a literal LAST segment cannot swallow a sibling
  // child route (`/api/tenant/{id}/locations`, `/api/tenant/{id}/managers`),
  // which a `startsWith` or an unanchored `includes` would.
  //
  // Without this branch `getTenantTranslationLanguages` falls through to a real
  // network call that nothing answers in fake mode, and the card sits
  // permanently in `design/states.md` §2b (load failed) — correct behaviour
  // against a dead endpoint, but it makes `npm run start:fake` unable to show
  // the feature at all.
  if (
    segments.length === 4 &&
    segments[0] === 'api' &&
    segments[1] === 'tenant' &&
    segments[3] === 'translation-languages'
  ) {
    if (method === 'GET') {
      return fakeGetTenantTranslationLanguages(segments[2]);
    }
    if (method === 'PUT') {
      // `?? []` rather than `|| []`: an EMPTY array is a real user action
      // ("translate into nothing", spec AT4) and must not be coerced away by a
      // falsy test. Only a missing key falls back, and the service layer never
      // sends one — omission is a 422 on the real route (ADR 0001 §7.3).
      const requested = (body as { translation_languages?: string[] } | undefined)
        ?.translation_languages;
      return fakeSetTenantTranslationLanguages(segments[2], requested ?? []);
    }
  }
  // feat-284 — exactly `GET /api/tenant/{id}/learn-content`, the read `/quiz-review`
  // makes. Same segment-count + literal-final-segment anchoring as the branch above,
  // for the same reason. READ ONLY: the curriculum EDITOR's writes go to other paths
  // (feat-299 now acknowledges the five text PUTs `/quiz-review` reuses — below).
  //
  // Without this, `/quiz-review` in fake mode is its red error box and nothing else,
  // which makes the prescribed `npm run start:fake` visual check impossible — and
  // `start:local` is not an option on this repo (it points Cognito at the PROD pool).
  //
  // `lang` is read from the QUERY STRING, which `path` above has already stripped.
  // It is a SINGLE code: the browser's Both-mode `?lang=en,es` never reaches the API
  // (slice brief §3a), and `getQuizContent` throws rather than send one.
  if (
    segments.length === 4 &&
    segments[0] === 'api' &&
    segments[1] === 'tenant' &&
    segments[3] === 'learn-content' &&
    method === 'GET'
  ) {
    const query = url.split('?')[1] ?? '';
    const lang = new URLSearchParams(query).get('lang') ?? undefined;
    return fakeGetLearnContent(segments[2], lang);
  }
  // feat-299 — the two feat-270 approval READS `/quiz-review` makes for its
  // pending chips: exactly `GET /api/tenant/{id}/learn-content/approvals/pending`
  // (6 segments) and `GET …/approvals/lessons/{lesson_id}` (7 segments). The
  // approve POST (8 segments, final `approve`) is deliberately NOT matched.
  if (
    method === 'GET' &&
    segments[0] === 'api' &&
    segments[1] === 'tenant' &&
    segments[3] === 'learn-content' &&
    segments[4] === 'approvals'
  ) {
    if (segments.length === 6 && segments[5] === 'pending') {
      return fakeGetApprovalQueue(segments[2]);
    }
    if (segments.length === 7 && segments[5] === 'lessons') {
      return fakeGetApprovalLessonDetail(segments[2], segments[6]);
    }
  }
  // feat-321 — exactly `GET /api/tenant/{id}/locations/{lid}/shift-leads`
  // (6 segments, literal final segment), same anchoring discipline as above —
  // it cannot swallow the sibling `/servers` or `/managers` children.
  if (
    method === 'GET' &&
    segments.length === 6 &&
    segments[0] === 'api' &&
    segments[1] === 'tenant' &&
    segments[3] === 'locations' &&
    segments[5] === 'shift-leads'
  ) {
    return fakeGetShiftLeads(segments[2], segments[4]);
  }
  // feat-325 — exactly `POST /api/admin/question-items/{id}/test-grade`
  // (5 segments, literal final segment): the editor's "Try an answer" box.
  if (
    method === 'POST' &&
    segments.length === 5 &&
    segments[0] === 'api' &&
    segments[1] === 'admin' &&
    segments[2] === 'question-items' &&
    segments[4] === 'test-grade'
  ) {
    return fakeTestGradeBlank((body || {}) as Parameters<typeof fakeTestGradeBlank>[0]);
  }
  // feat-361 — exactly `POST /api/learn-drills/{id}/move` (4 segments, literal
  // final segment): the curriculum editor's "Move to Lesson…" action.
  if (
    method === 'POST' &&
    segments.length === 4 &&
    segments[0] === 'api' &&
    segments[1] === 'learn-drills' &&
    segments[3] === 'move'
  ) {
    return fakeMoveDrill(segments[2], String((body as { target_lesson_id?: string } | undefined)?.target_lesson_id ?? ''));
  }
  // feat-299 — the five Edit-mode text writes `/quiz-review` reuses, acknowledged
  // (not persisted) so an inline save shows "Saved" with no backend. Each is
  // anchored by segment count and literal collection segment:
  //   PUT /api/question-items/{id}  /api/rehearsals/{id}  /api/learn-lessons/{id}
  //   PUT /api/learn-drills/{id}    /api/admin/learn-content/reading-items/{id}
  if (method === 'PUT' && segments[0] === 'api') {
    if (
      segments.length === 3 &&
      ['question-items', 'rehearsals', 'learn-lessons', 'learn-drills'].includes(segments[1])
    ) {
      return { status: 'ok' };
    }
    if (
      segments.length === 5 &&
      segments[1] === 'admin' &&
      segments[2] === 'learn-content' &&
      segments[3] === 'reading-items'
    ) {
      return { status: 'ok' };
    }
  }
  return null;
}

/**
 * feat-243 — the error `fetchApi` throws, with the response's status and parsed
 * body kept rather than thrown away.
 *
 * ADDITIVE ON PURPOSE. `fetchApi` is shared by every admin service in this repo
 * and every existing caller reads only `.message`. `ApiError extends Error`, so
 * `e instanceof Error` and `e.message` behave exactly as they did — **and
 * `.message` is computed by the unchanged code below, byte for byte, on every
 * path.** That is the contract AC17 asserts, string-`detail`, array-`detail`
 * (FastAPI 422) and unparseable-body alike.
 *
 * WHY IT EXISTS. `PUT /api/learn-tracks/{id}/lessons` and `/prereqs` now answer
 * **409** with a structured object `detail` (spec.md §3a) describing what
 * diverged. An object `detail` falls into the `else` below, is assigned to a
 * string variable, and renders as `[object Object]` — the diff the surface needs
 * was being destroyed one line before the throw. `status` and `body` are how a
 * caller gets at it.
 *
 * NO ENDPOINT KNOWLEDGE LIVES HERE. Interpreting the 409 shape is the tracks
 * surface's business; this file's only job is to stop discarding it.
 *
 * `Object.setPrototypeOf` is required, not decoration: this project targets ES5
 * (`tsconfig.json`), where subclassing a built-in leaves the instance's
 * prototype as `Error.prototype` and `instanceof ApiError` silently returns
 * false.
 */
export class ApiError extends Error {
  /** HTTP status of the failed response. */
  readonly status: number;
  /** The parsed JSON body, or the raw text when the body would not parse. */
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

/**
 * feat-243 SECURITY HIGH-2 — log the STATUS and the DERIVED MESSAGE. Never the
 * parsed body.
 *
 * `console.error('API Error Response:', errorData)` used to hand the whole parsed
 * body to Sentry's breadcrumbs integration, which stores the **raw argument
 * objects** rather than only the rendered string
 * (`@sentry/browser/.../integrations/breadcrumbs.js`:
 * `data: { arguments: handlerData.args, logger: 'console' }`). The rendered
 * `message` is `[object Object]`, which is exactly what made it look harmless;
 * the payload rode in `data.arguments`, which Replay re-serializes at normalize
 * depth 7 — and this feature's 409 puts `changed_by`, a real person's email
 * address, at depth ~3. Neither existing defence covers that channel:
 * `networkCaptureBodies: false` closes the network-body path only, and **no DOM
 * masking attribute can reach a console breadcrumb.** This protects every admin
 * service that throws through `fetchApi`, not only the tracks surface.
 *
 * ⚠️ THE `String()` IS THE WHOLE FIX, NOT TIDINESS. `errorMessage` is declared
 * untyped and an OBJECT `detail` is assigned straight to it — that is precisely
 * why an unhandled 409 renders as `[object Object]`. So "log `errorMessage`, not
 * `errorData`" passes the conflict body to `console.error` **unchanged** for
 * exactly the response that motivated this finding, and the fix would be a no-op
 * where it matters most. Coercing at the log site collapses it to
 * `[object Object]` before the breadcrumb integration ever sees an object.
 *
 * Nothing here touches what is THROWN: `new ApiError(errorMessage, …)` still
 * receives the original value, so `.message` is byte-identical on every path
 * (AC17).
 */
function logApiError(label: string, status: number, message: unknown): void {
  console.error(label, status, String(message));
}

/**
 * Fetch data from API with error handling
 */
export async function fetchApi<T>(
  url: string,
  options: ApiOptions = {}
): Promise<T> {
  const { method = "GET", headers = {}, body } = options;

  // feat-196 (B3) — fake mode serves the tenant surface locally so the Admin-only
  // "Live client" control is demonstrable with no backend (AC12/AC13). Returns null
  // for every other path, which then takes the normal network route below.
  const faked = fakeTenantResponse(url, method, body);
  if (faked !== null) {
    return faked as T;
  }

  // Build API URL using the same configuration as the rest of the app
  const protocol = config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  const baseUrl = `${protocol}://${config.restaurantBackendHost}`;
  const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

  const requestOptions: RequestInit = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...headers,
    },
    credentials: "include", // Changed to include for cross-origin cookies
  };

  if (body) {
    requestOptions.body = JSON.stringify(body);
  }

  // feat-028: route through the shared wrapper so these admin-tool calls
  // (TenantDashboard, DemoSetup) get proactive refresh + 401-retry-once too.
  const response = await authedApiFetch(fullUrl, requestOptions);

  // Check if the response is ok
  if (!response.ok) {
    // Try to get error message from response
    let errorMessage;
    // feat-243 — kept alongside the message, never in place of it. Every
    // assignment to `errorMessage` below is exactly as it was.
    let errorBody: unknown;
    try {
      const errorData = await response.json();
      errorBody = errorData;
      // FastAPI 422 returns `detail` as an array of {loc, msg, type, ...} objects.
      // Plain HTTPException(detail=...) returns it as a string. Handle both.
      const detail = errorData.detail;
      if (Array.isArray(detail)) {
        errorMessage =
          detail
            .map((d: { loc?: (string | number)[]; msg?: string }) => {
              const path = (d.loc || [])
                .filter((p) => p !== 'body')
                .join('.');
              return path ? `${path}: ${d.msg}` : (d.msg ?? '');
            })
            .filter(Boolean)
            .join('; ') || `API error: ${response.status}`;
      } else {
        errorMessage =
          detail || errorData.error || errorData.message || `API error: ${response.status}`;
      }
      logApiError('API Error Response:', response.status, errorMessage);
    } catch (parseError) {
      // If JSON parsing fails, try to get text response
      try {
        const errorText = await response.text();
        errorBody = errorText;
        errorMessage = errorText || `API error: ${response.status} ${response.statusText}`;
        logApiError('API Error Response (text):', response.status, errorMessage);
      } catch {
        errorMessage = `API error: ${response.status} ${response.statusText}`;
        // The body could be read neither as JSON nor as text — which is also
        // where control lands if message derivation above ever throws, since
        // `response.text()` then fails on an already-consumed body. Before the
        // HIGH-2 fix moved the log after derivation, this path always logged
        // something; without this line it logs NOTHING, and a silent failure in
        // the one client every admin service throws through is precisely the
        // property that made HIGH-2 worth fixing. Status and rendered message
        // only, same rule as the two calls above.
        logApiError('API Error Response (unreadable):', response.status, errorMessage);
      }
    }

    throw new ApiError(errorMessage, response.status, errorBody);
  }

  return response.json();
}
